/* eslint-disable */

import React, { useRef, useEffect, useCallback } from 'react'
import { Box, Paper, Typography } from '@mui/material'

export interface ColorChartData {
  [key: string]: {
    backgroundColor: string;
  };
}

export interface CellDimensions {
  width: number;
  height: number;
  margin: number;
}

interface ColorChartCanvasProps {
  width: number;
  height: number;
  data: ColorChartData;
  zoom: number;
  tool: 'brush' | 'bucket' | 'text';
  cellDimensions: CellDimensions;
  onMouseDown: (i: number, j: number) => void;
  onMouseEnter: (i: number, j: number) => void;
  onMouseUp: () => void;
  getColorAt: (i: number, j: number) => string;
  textInput?: string;
  getTextPreviewCells?: (i: number, j: number) => Array<[number, number]>;
  color?: string;
  canvasRef?: React.RefObject<HTMLDivElement>;
}

export default function ColorChartCanvas({
  width,
  height,
  data,
  zoom,
  tool,
  cellDimensions,
  onMouseDown,
  onMouseEnter,
  onMouseUp,
  getColorAt,
  textInput,
  getTextPreviewCells,
  color,
  canvasRef: externalRef
}: ColorChartCanvasProps) {
  const internalRef = useRef<HTMLDivElement>(null);
  const containerRef = externalRef || internalRef;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hoveredCellRef = useRef<[number, number] | null>(null);
  const isPaintingRef = useRef(false);

  // Calculate canvas dimensions
  const cellWidth = cellDimensions.width;
  const cellHeight = cellDimensions.height;
  const cellMargin = cellDimensions.margin;
  const totalCellWidth = cellWidth + cellMargin * 2;
  const totalCellHeight = cellHeight + cellMargin * 2;
  const canvasWidth = width * totalCellWidth;
  const canvasHeight = height * totalCellHeight;

  // Convert mouse coordinates to grid cell
  const getCellFromMouse = useCallback((event: React.MouseEvent<HTMLCanvasElement> | MouseEvent): [number, number] | null => {
    if (!canvasRef.current) return null;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Get mouse position relative to canvas
    // getBoundingClientRect() already accounts for CSS transforms (zoom)
    // But we need to convert back to the original canvas size
    const x = (event.clientX - rect.left) / zoom;
    const y = (event.clientY - rect.top) / zoom;
    
    // Calculate which cell was clicked
    const col = Math.floor(x / totalCellWidth);
    const row = Math.floor(y / totalCellHeight);
    
    if (row >= 0 && row < height && col >= 0 && col < width) {
      return [row, col];
    }
    
    return null;
  }, [width, height, totalCellWidth, totalCellHeight, zoom]);

  // Convert touch coordinates to grid cell
  const getCellFromTouch = useCallback((event: React.TouchEvent<HTMLCanvasElement> | TouchEvent): [number, number] | null => {
    if (!canvasRef.current) return null;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Get the first touch point
    const touch = event.touches[0] || event.changedTouches[0];
    if (!touch) return null;
    
    // Get touch position relative to canvas
    const x = (touch.clientX - rect.left) / zoom;
    const y = (touch.clientY - rect.top) / zoom;
    
    // Calculate which cell was touched
    const col = Math.floor(x / totalCellWidth);
    const row = Math.floor(y / totalCellHeight);
    
    if (row >= 0 && row < height && col >= 0 && col < width) {
      return [row, col];
    }
    
    return null;
  }, [width, height, totalCellWidth, totalCellHeight, zoom]);

  // Draw the entire chart
  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !width || !height) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw all cells
    for (let i = 0; i < height; i++) {
      for (let j = 0; j < width; j++) {
        const x = j * totalCellWidth + cellMargin;
        const y = i * totalCellHeight + cellMargin;
        
        // Get cell color
        const color = getColorAt(i, j);
        
        // Draw cell background
        ctx.fillStyle = color;
        ctx.fillRect(x, y, cellWidth, cellHeight);
        
        // Draw border
        ctx.strokeStyle = 'rgba(0,0,0,0.1)';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, cellWidth, cellHeight);
      }
    }
    
    // Draw hover effect or text preview
    const hovered = hoveredCellRef.current;
    if (hovered) {
      const [row, col] = hovered;
      
      if (tool === 'text' && getTextPreviewCells && textInput?.trim()) {
        // Draw text preview
        const previewCells = getTextPreviewCells(row, col);
        previewCells.forEach(([r, c]) => {
          const x = c * totalCellWidth + cellMargin;
          const y = r * totalCellHeight + cellMargin;
          
          // Draw semi-transparent preview overlay
          ctx.fillStyle = color ? `${color}80` : 'rgba(0,0,0,0.3)';
          ctx.fillRect(x, y, cellWidth, cellHeight);
          
          // Draw preview border
          ctx.strokeStyle = color || 'rgba(0,0,0,0.5)';
          ctx.lineWidth = 1;
          ctx.strokeRect(x, y, cellWidth, cellHeight);
        });
      } else {
        // Draw regular hover effect
        const x = col * totalCellWidth + cellMargin;
        const y = row * totalCellHeight + cellMargin;
        
        ctx.strokeStyle = 'rgba(0,0,0,0.3)';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, cellWidth, cellHeight);
      }
    }
  }, [width, height, cellWidth, cellHeight, cellMargin, totalCellWidth, totalCellHeight, getColorAt, tool, getTextPreviewCells, textInput, color]);

  // Redraw when data, zoom, hover, textInput, or color changes (batched with requestAnimationFrame)
  useEffect(() => {
    let rafId: number;
    const scheduleRedraw = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        drawChart();
      });
    };
    
    scheduleRedraw();
    
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [data, zoom, textInput, color, drawChart]);

  // Set up canvas size
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    
    canvas.width = canvasWidth * dpr;
    canvas.height = canvasHeight * dpr;
    
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.scale(dpr, dpr);
    }
    
    drawChart();
  }, [canvasWidth, canvasHeight, drawChart]);

  // Mouse event handlers
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const cell = getCellFromMouse(e);
    if (cell) {
      isPaintingRef.current = true;
      onMouseDown(cell[0], cell[1]);
    }
  }, [getCellFromMouse, onMouseDown]);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const cell = getCellFromMouse(e);
    
    // Update hover effect
    if (hoveredCellRef.current?.[0] !== cell?.[0] || hoveredCellRef.current?.[1] !== cell?.[1]) {
      hoveredCellRef.current = cell;
      drawChart();
    }
    
    // Handle painting
    if (isPaintingRef.current && cell) {
      onMouseEnter(cell[0], cell[1]);
    }
  }, [getCellFromMouse, onMouseEnter, drawChart]);

  const handleMouseUp = useCallback(() => {
    isPaintingRef.current = false;
    onMouseUp();
  }, [onMouseUp]);

  const handleMouseLeave = useCallback(() => {
    hoveredCellRef.current = null;
    isPaintingRef.current = false;
    drawChart();
  }, [drawChart]);

  // Set cursor style
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      if (tool === 'bucket') {
        canvas.style.cursor = 'pointer';
      } else if (tool === 'text') {
        canvas.style.cursor = 'text';
      } else {
        canvas.style.cursor = 'crosshair';
      }
    }
  }, [tool]);

  // Touch event handlers
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent scrolling/zooming and long-press menu
    e.stopPropagation();
    const cell = getCellFromTouch(e);
    if (cell) {
      isPaintingRef.current = true;
      onMouseDown(cell[0], cell[1]);
    }
  }, [getCellFromTouch, onMouseDown]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault(); // Prevent scrolling/zooming
    e.stopPropagation();
    const cell = getCellFromTouch(e);
    
    // Update hover effect
    if (hoveredCellRef.current?.[0] !== cell?.[0] || hoveredCellRef.current?.[1] !== cell?.[1]) {
      hoveredCellRef.current = cell;
      drawChart();
    }
    
    // Handle painting
    if (isPaintingRef.current && cell) {
      onMouseEnter(cell[0], cell[1]);
    }
  }, [getCellFromTouch, onMouseEnter, drawChart]);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    isPaintingRef.current = false;
    hoveredCellRef.current = null;
    onMouseUp();
    drawChart();
  }, [onMouseUp, drawChart]);

  const handleTouchCancel = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    isPaintingRef.current = false;
    hoveredCellRef.current = null;
    drawChart();
  }, [drawChart]);

  // Prevent context menu (long-press menu on iOS)
  const handleContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }, []);

  // Prevent text selection and context menu on canvas, and add native touch listeners
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Prevent context menu (long-press menu)
    const handleContextMenu = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    // Prevent text selection
    const handleSelectStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    // Prevent drag (which can trigger text selection)
    const handleDragStart = (e: Event) => {
      e.preventDefault();
      return false;
    };

    // Native touch event handlers with passive: false to ensure preventDefault works
    // Only preventDefault, don't stopPropagation so React handlers still fire
    const handleNativeTouchStart = (e: TouchEvent) => {
      e.preventDefault();
    };

    const handleNativeTouchMove = (e: TouchEvent) => {
      e.preventDefault();
    };

    const handleNativeTouchEnd = (e: TouchEvent) => {
      e.preventDefault();
    };

    canvas.addEventListener('contextmenu', handleContextMenu, { passive: false });
    canvas.addEventListener('selectstart', handleSelectStart, { passive: false });
    canvas.addEventListener('dragstart', handleDragStart, { passive: false });
    canvas.addEventListener('touchstart', handleNativeTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleNativeTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleNativeTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleNativeTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('contextmenu', handleContextMenu);
      canvas.removeEventListener('selectstart', handleSelectStart);
      canvas.removeEventListener('dragstart', handleDragStart);
      canvas.removeEventListener('touchstart', handleNativeTouchStart);
      canvas.removeEventListener('touchmove', handleNativeTouchMove);
      canvas.removeEventListener('touchend', handleNativeTouchEnd);
      canvas.removeEventListener('touchcancel', handleNativeTouchEnd);
    };
  }, []);

  // Handle window mouseup to stop painting
  useEffect(() => {
    const handleWindowMouseUp = () => {
      isPaintingRef.current = false;
    };
    
    const handleWindowTouchEnd = () => {
      isPaintingRef.current = false;
    };
    
    window.addEventListener('mouseup', handleWindowMouseUp);
    window.addEventListener('touchend', handleWindowTouchEnd);
    return () => {
      window.removeEventListener('mouseup', handleWindowMouseUp);
      window.removeEventListener('touchend', handleWindowTouchEnd);
    };
  }, []);


  return (
    <Paper 
      ref={containerRef}
      elevation={1} 
      sx={{ 
        height: '100%', 
        overflow: 'auto',
        display: 'block',
        backgroundColor: '#f5f5f5',
        position: 'relative',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}
    >
      { (width && height) ? (
        <Box 
          sx={{
            width: `${canvasWidth * zoom + 32 * zoom}px`,
            height: `${canvasHeight * zoom + 32 * zoom}px`,
            minWidth: `${canvasWidth * zoom + 32 * zoom}px`,
            minHeight: `${canvasHeight * zoom + 32 * zoom}px`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: `${16 * zoom}px`,
            margin: '0 auto'
          }}
        >
          <Box 
            sx={{ 
              display: 'inline-block', 
              padding: 2,
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease-out'
            }}
          >
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchCancel}
              onContextMenu={handleContextMenu}
              style={{
                display: 'block',
                width: `${canvasWidth}px`,
                height: `${canvasHeight}px`,
                touchAction: 'none',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                WebkitTouchCallout: 'none',
                msUserSelect: 'none'
              }}
            />
          </Box>
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', padding: 4 }}>
          <Typography variant="h6" color="text.secondary">
            Add dimensions to generate grid
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Use the sliders on the left to set height and width, then use the color picker to design your chart!
          </Typography>
        </Box>
      )}
    </Paper>
  )
}

